import { AppRegistry, CreateSfApp } from '../types'
import type { CreateAppFunction } from 'vue'

import makeAppRegistry from './app-registry'
import { createApp as createAppInternal } from './create-app-internal'

/**
 * Adapter over Vue's createApp function that allows for singleton apps
 * It preconfigures each instance with sensible SF defaults
 *
 * Allows for the creation of Singleton apps, which make sure that older instances are unmounted when new createApp calls are made
 * To create a Singleton app you must pass a unique singletonId in the meta object
 * Singleton apps get persisted in the AppRegistry
 * Singleton apps are cleared when navigating away from the page with Turbolinks
 * @param AppRegistry
 * @param createAppFunction
 * @returns
 */
export const createAppWithRegistry = (
  AppRegistry: AppRegistry,
  createAppFunction: CreateAppFunction<Element>
): CreateSfApp => {
  AppRegistry.configureRegistry()

  return (Component, props, meta) => {
    const { singletonId } = meta || {}

    if (singletonId) {
      const app = AppRegistry.retrieveApp(singletonId)

      if (app) {
        app.unmount()
      }

      const newApp = createAppFunction(Component, props)

      AppRegistry.persistApp(newApp, singletonId)

      return newApp
    }

    return createAppFunction(Component, props)
  }
}

export const createApp = createAppWithRegistry(
  makeAppRegistry(),
  createAppInternal
)
